
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaTimes, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_SERVER_URL + '/api/users';

function Users() {
    const [users, setUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'user', points: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const navigate = useNavigate();

    // Fetch all users
    const fetchUsers = async () => {
        try {
            const res = await axios.get(apiUrl);
            setUsers(res.data);
        } catch (err) {
            setError('Failed to fetch users');
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle form input changes
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Clear form & editing state
    const resetForm = () => {
        setForm({ name: '', email: '', phone: '', password: '', role: 'user', points: 0 });
        setEditingUser(null);
        setError('');
        setSuccessMsg('');
    };

    // Submit form (create or update) with validation
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMsg('');

        // Validation
        if (!form.name || form.name.trim().length < 2) {
            setError('Name must be at least 2 characters');
            setLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!form.email || !emailRegex.test(form.email)) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        if (form.phone && !/^\d{0,15}$/.test(form.phone)) {
            setError('Phone must be numeric and up to 15 digits');
            setLoading(false);
            return;
        }

        if (!editingUser && (!form.password || form.password.length < 6)) {
            setError('Password is required and must be at least 6 characters for new users');
            setLoading(false);
            return;
        }

        if (form.points < 0 || isNaN(form.points)) {
            setError('Points must be a non-negative number');
            setLoading(false);
            return;
        }

        try {
            if (editingUser) {
                await axios.put(`${apiUrl}/${editingUser.id}`, form);
                setSuccessMsg('User updated successfully');
            } else {
                await axios.post(apiUrl, form);
                setSuccessMsg('User created successfully');
            }

            resetForm();
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.msg || 'Error submitting form');
        } finally {
            setLoading(false);
        }
    };

    // Delete user
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            await axios.delete(`${apiUrl}/${id}`);
            setSuccessMsg('User deleted successfully');
            fetchUsers();
        } catch {
            setError('Failed to delete user');
        }
    };

    // Edit user
    const handleEdit = (user) => {
        setEditingUser(user);
        setForm({
            name: user.name,
            email: user.email,
            phone: user.phone,
            password: '',
            role: user.role || 'user',
            points: user.points || 0
        });
        setError('');
        setSuccessMsg('');
    };

    // View user details
    const handleView = (id) => {
        navigate(`/admin/userdetails/${id}`);
    };

    const filteredUsers = users.filter((u) => {
    const search = (searchTerm || '').toLowerCase();

    const name = typeof u.name === 'string' ? u.name.toLowerCase() : '';
    const email = typeof u.email === 'string' ? u.email.toLowerCase() : '';
    const phone = typeof u.phone === 'string' ? u.phone : '';

    return (
        name.includes(search) ||
        email.includes(search) ||
        phone.includes(search)
    );
});


    return (
        <div className="container p-5">
            <h3>👥 Users Management</h3>

            {error && <div className="alert alert-danger">{error}</div>}
            {successMsg && <div className="alert alert-success">{successMsg}</div>}

            {/* User Form */}
            <form onSubmit={handleSubmit} className="m-4">
                <h5>{editingUser ? 'Edit User' : 'Add New User'}</h5>

                <div className="row g-3">
                    <div className="col-md-6">
                        <label>Name</label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label>Email</label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label>Phone</label>
                        <input
                            type="text"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            className="form-control"
                        />
                    </div>

                    <div className="col-md-6">
                        <label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</label>
                        <input
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            className="form-control"
                            {...(!editingUser && { required: true })}
                        />
                    </div>

                    <div className="col-md-6">
                        <label>Role</label>
                        <select
                            name="role"
                            value={form.role}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label>Points</label>
                        <input
                            type="number"
                            name="points"
                            value={form.points}
                            onChange={handleChange}
                            className="form-control"
                            min={0}
                        />
                    </div>
                </div>

                <div className="mt-3 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Add User')}
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={resetForm}
                        disabled={loading}
                    >
                        <FaTimes /> Cancel
                    </button>
                </div>
            </form>

            <hr />

            {/* Search Input */}
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Users Table */}
            {filteredUsers.length === 0 ? (
                <p>No users found.</p>
            ) : (
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Role</th>
                            <th>Points</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(u => (
                            <tr key={u.id}>
                                <td>{u.name}</td>
                                <td>{u.email}</td>
                                <td>{u.phone}</td>
                                <td>{u.role}</td>
                                <td>{u.points}</td>
                                <td>
                                    {/* View button */}
                                    <button
                                        className="btn btn-sm btn-outline-success me-2"
                                        onClick={() => handleView(u.id)}
                                        disabled={loading}
                                        title="View User"
                                    >
                                        <FaEye />
                                    </button>

                                    {/* Edit button */}
                                    <button
                                        className="btn btn-sm btn-outline-primary me-2"
                                        onClick={() => handleEdit(u)}
                                        disabled={loading}
                                        title="Edit User"
                                    >
                                        <FaEdit />
                                    </button>

                                    {/* Delete button */}
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(u.id)}
                                        disabled={loading}
                                        title="Delete User"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default Users;
